import { Component, OnInit, OnDestroy } from '@angular/core';

import {
  of,
  tap,
  filter,
  switchMap,
  catchError,
  Subscription,
  combineLatest,
  BehaviorSubject,
  startWith,
  pairwise,
  map,
  combineLatestWith, take,
} from 'rxjs';

import { makeMap } from '@shared/core';
import { Location } from '@shared/location/models';
import { Invite } from '@shared/invite/models';
import { InviteStatus, InviteRole } from '@shared/invite/enums';
import { AuthUser } from '@shared/signup/models';
import {
  ConfirmationModalComponent, ConfirmationModalResult,
  DropdownFilterItem,
  UserWithAccount,
} from '@tablesready/common-ui';
import { InviteApiService } from '../../../../../services';
import { EditUserAccessService, InvitesService } from '../../services';
import { InviteStatuses, InviteStatusesMap } from '../../../../constants';
import { InviteUserComponent } from '../../modals';
import { InvitesFilters } from '../../models';
import { InvitesFilterService } from './invites-filter.service';
import { InviteRoles } from './constants';
import { UsersListService } from '../users-list/users-list.service';
import { FrontendModalService } from '@tablesready/frontend-modal';
import {
  AuthRestaurantService,
  InviteRolesMap,
  LocationsService,
} from '@tablesready/frontend-store';
import { AlertsService } from '@tablesready/frontend-alerts-bar';

@Component({
  selector: 'tablesready-invites-list',
  templateUrl: './invites-list.component.html',
  styleUrls: ['./invites-list.component.scss'],
})
export class InvitesListComponent implements OnInit, OnDestroy {
  page = 1;
  pageSize = 10;
  maxSize = 10;
  invites: Invite[] = [];
  inviteStatus = InviteStatus;
  locations: Location[];
  inviteStatusesMap = InviteStatusesMap;
  inviteRolesMap = InviteRolesMap;
  inviteRoles = InviteRoles;
  inviteStatuses = InviteStatuses;
  dropdownLocations: DropdownFilterItem<string | null>[] = [];
  locationsMap: Record<string, Location>;
  inviteLocationsTooltips: Record<string, string> = {};
  timezone = '';
  inviteUserFilter?: string;
  locationFilter?: string;
  statusFilter?: string;
  private currentUser: AuthUser;

  filters$ = new BehaviorSubject<InvitesFilters>({
    email: '',
    role: null,
    location: null,
    status: null,
  });

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly alertsService: AlertsService,
    private readonly modalController: FrontendModalService,
    private readonly locationsService: LocationsService,
    private readonly invitesService: InvitesService,
    private readonly inviteApiService: InviteApiService,
    private readonly invitesFilterService: InvitesFilterService,
    private readonly usersListService: UsersListService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly editUserAccessService: EditUserAccessService,
  ) {}

  ngOnInit(): void {
    const selectInvitesSub = this.authRestaurantService.select()
      .pipe(
        startWith(null),
        pairwise(),
        filter(([prev, current]) => current.account !== undefined && prev?.account?.id !== current.account?.id),
        tap(([,user]) => {
          this.currentUser = user;
          this.timezone = user.location.timezone;
        }),
        switchMap(() => combineLatest([
          this.invitesService.load().pipe(switchMap(() => this.invitesService.selectAll())),
          this.filters$.pipe(
            startWith(null),
            pairwise(),
            filter(([prev, current]) => prev === null
              || prev.email !== current.email
              || prev.location !== current.location
              || prev.role !== current.role
              || prev.status !== current.status,
            ),
            map(([, current]) => current),
          ),
          this.locationsService.selectAll().pipe(tap(locations => {
            this.locations = locations;
            this.locationsMap = makeMap(locations, '_id');
            this.dropdownLocations = locations.map(({_id, name}) => ({value: _id, title: name}));
          })),
        ])),
        tap(([invites, filters]) => {
          this.invites = this.invitesFilterService.filter(filters, invites);
          for (let invite of invites) {
            if (!invite.locations) {
              continue;
            }

            const locationsNames = [];
            const inviteLocations: string[] = [];
            for (let locationId of invite.locations) {
              const location = this.locationsMap[locationId];
              if (location) {
                locationsNames.push(this.locationsMap[locationId].name);
                inviteLocations.push(locationId);
              }
            }
            invite.locations = inviteLocations;

            this.inviteLocationsTooltips[invite.id] = locationsNames.join(', ');
          }
        }),
      )
      .subscribe();
    this.subscriptions.add(selectInvitesSub);
    this.subscriptions.add(this.usersListService.listenToChanges().subscribe());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  inviteUser(): void {
    this.modalController.create<InviteUserComponent, UserWithAccount | undefined>(InviteUserComponent, {
      currentUser: this.currentUser,
      data: this.usersListService.getUsersAndLocations()
        .pipe(
          combineLatestWith(this.invitesService.selectAll()),
          map(([[users, locations], invites]) => [users, locations, invites]),
        ),
    })
      .pipe(
        take(1),
        filter(data => !!data),
        tap((user: UserWithAccount) => this.editUserAccessService.editUserAccess(user)),
      )
      .subscribe();
  }

  resend({ id, email }: Invite): void {
    this.modalController.create<ConfirmationModalComponent, ConfirmationModalResult<boolean>>(ConfirmationModalComponent, {
      options: {
        title: `Resend invite (${email})`,
        content: 'Are you sure to resend this invite?',
      },
    })
      .pipe(
        take(1),
        filter((res) => res?.response),
        switchMap(() => this.inviteApiService.resend(id)),
        tap(() => this.alertsService.addSuccess('resent invite', 'Success: ')),
        catchError(() => {
          this.alertsService.addError('failed resend invite');

          return of(null);
        }),
      )
      .subscribe();
  }

  cancel({ id, email }: Invite): void {
    this.modalController.create<ConfirmationModalComponent, ConfirmationModalResult<boolean>>(ConfirmationModalComponent, {
      options: {
        title: `Cancel invite (${email})`,
        content: 'Are you sure to cancel this invite?',
      },
    })
      .pipe(
        take(1),
        filter((res) => res?.response),
        switchMap(() => this.inviteApiService.remove(id)),
        tap(() => this.alertsService.addSuccess('invite canceled', 'Success: ')),
        catchError(() => {
          this.alertsService.addError('failed cancel invite');

          return of(null);
        }),
      )
      .subscribe();
  }

  searchInvite(email: string): void {
    let filters = this.filters;
    filters = { ...filters, email };

    this.filters$.next(filters);
  }

  setFilters(item: Partial<InvitesFilters>): void {
    this.filters$.next({...this.filters, ...item});
  }

  get filters(): InvitesFilters {
    return this.filters$.getValue();
  }

  get start(): number {
    return (this.page - 1) * this.pageSize;
  }

  get end(): number {
    return (this.page - 1) * this.pageSize + this.pageSize;
  }
}
